import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { Lock as LockIcon, Logout as LogoutIcon } from '@mui/icons-material';
import ThemeBackground from '../components/Layout/ThemeBackground';
import CredentialRenewal from '../components/Counsellor/CredentialRenewal';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

interface SuspensionStatus {
  applicationStatus: string;
  suspensionCause?: string;
  suspensionReason?: string;
  suspendedDate?: string;
  remainingExpiredCredentials?: string[];
  pendingSubmissions?: { credentialType: string; submittedDate: string }[];
}

/**
 * Restricted portal for suspended counsellors. The backend
 * (suspendedCounsellorGuard) confines the account to this surface —
 * this page is everything they need to remediate: why they were
 * suspended, what credential is missing/expired, and the submission form.
 */
const SuspendedCompliance: React.FC = () => {
  const [status, setStatus] = useState<SuspensionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const { data } = await api.get<SuspensionStatus>('/api/credential-reactivation/status');
        setStatus(data);
        // If the record is no longer suspended (e.g. admin reactivated),
        // send them back into the app.
        if (data.applicationStatus !== 'Suspended') {
          navigate('/counsellor/dashboard', { replace: true });
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load account status');
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  return (
    <ThemeBackground variant="full">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="glass-card p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Box sx={{ color: '#f87171', mt: 0.5 }}>
                <LockIcon />
              </Box>
              <div>
                <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 700 }}>
                  Account Suspended
                </Typography>
                {status?.suspensionReason && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                    {status.suspensionReason}
                  </Typography>
                )}
                {status?.suspendedDate && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Suspended on {new Date(status.suspendedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </Typography>
                )}
              </div>
            </div>
            <Button
              size="small"
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                color: 'rgba(255,255,255,0.8)',
                borderColor: 'rgba(255,255,255,0.3)',
                textTransform: 'none',
                flexShrink: 0
              }}
            >
              Sign out
            </Button>
          </div>
        </div>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{
                mb: 2,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#f87171',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                '& .MuiAlert-icon': { color: '#f87171' }
              }}>
                {error}
              </Alert>
            )}

            <Alert severity="warning" sx={{
              mb: 3,
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              color: '#fcd34d',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              '& .MuiAlert-icon': { color: '#fbbf24' }
            }}>
              Your access is limited to this compliance area until your credentials are renewed and approved.
              Upload the renewed document below — the admin team will review it and restore full access.
            </Alert>

            {status?.pendingSubmissions && status.pendingSubmissions.length > 0 && (
              <Alert severity="info" sx={{
                mb: 3,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                color: '#93c5fd',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                '& .MuiAlert-icon': { color: '#60a5fa' }
              }}>
                You have {status.pendingSubmissions.length} submission{status.pendingSubmissions.length !== 1 ? 's' : ''} awaiting admin review
                ({status.pendingSubmissions.map(s => s.credentialType).join(', ')}). You do not need to resubmit unless we contact you.
              </Alert>
            )}

            <CredentialRenewal />
          </>
        )}
      </div>
    </ThemeBackground>
  );
};

export default SuspendedCompliance;
